import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, MapPin, Send, CheckCircle2 } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, Eyebrow } from "../components/Reveal";
import { SERVICES, SITE } from "../lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  company: "",
  phone: "",
  email: "",
  location: "",
  service: "",
  contact_method: "Phone",
  message: "",
  consent: false,
};

const inputClass =
  "w-full border border-black/15 bg-white px-4 py-3 text-sm text-coal placeholder:text-black/30 focus:border-ember focus:outline-none transition-colors";

const Field = ({ label, children, testId }) => (
  <label className="block" data-testid={testId}>
    <span className="mb-2 block text-xs font-bold uppercase tracking-[0.2em] text-neutral-500">{label}</span>
    {children}
  </label>
);

const Contact = () => {
  const [form, setForm] = useState(INITIAL);
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) =>
    setForm({ ...form, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, { ...form, service: form.service || "General Inquiry" });
      setStatus("sent");
      toast.success("Request received. Petroline Services will be in touch.");
    } catch (err) {
      setStatus("idle");
      toast.error("Something went wrong. Please try again or call us directly.");
    }
  };

  return (
    <main data-testid="contact-page">
      <Seo
        title="Contact Petroline Services | Request Service | Milton, ON"
        description="Contact Petroline Services for commercial repairs, renovations, preventive maintenance or urgent facility issues at petroleum retail locations across Southern Ontario. Call 905-514-9025."
      />

      <section className="mx-auto max-w-7xl px-5 pb-20 pt-24 lg:px-8 lg:pt-32">
        <Reveal>
          <Eyebrow>Contact</Eyebrow>
          <h1 className="max-w-3xl font-display text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl lg:text-6xl">
            Let's Talk About Your <span className="text-ember">Next Project</span>
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-mist md:text-lg">
            Whether you need a repair, renovation, preventive maintenance or support for an
            urgent facility issue, contact Petroline Services to discuss your requirements.
          </p>
        </Reveal>
      </section>

      <section className="border-t border-black/10 bg-ink-surface">
        <div className="mx-auto grid max-w-7xl gap-14 px-5 py-20 lg:grid-cols-5 lg:gap-20 lg:px-8 lg:py-28">
          <Reveal className="lg:col-span-2">
            <h2 className="font-display text-2xl font-extrabold tracking-tight">Petroline Services</h2>
            <p className="mt-6 flex items-start gap-3 text-sm leading-relaxed text-neutral-600">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-ember" />
              <span>
                {SITE.addressLine1}
                <br />
                {SITE.addressLine2}
              </span>
            </p>
            <a
              href={SITE.phoneHref}
              data-testid="contact-phone-link"
              className="mt-6 flex items-center gap-3 font-display text-2xl font-bold text-coal transition-colors hover:text-ember"
            >
              <Phone className="h-5 w-5 text-ember" />
              {SITE.phone}
            </a>
            <div className="mt-10 flex flex-col gap-4">
              <a
                href={SITE.phoneHref}
                data-testid="contact-call-now-btn"
                className="inline-flex items-center justify-center gap-3 bg-ember px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-coal transition-transform duration-200 hover:-translate-y-0.5"
              >
                <Phone className="h-4 w-4" />
                Call Now
              </a>
              <p className="text-xs leading-relaxed text-neutral-400">
                Serving Milton, Mississauga, Oakville, Burlington, Brampton, Vaughan, Toronto,
                Hamilton, Guelph, Cambridge, Kitchener-Waterloo and surrounding communities.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-3">
            {status === "sent" ? (
              <div
                data-testid="contact-success-message"
                className="flex h-full flex-col items-start justify-center border border-ember/40 bg-white p-10"
              >
                <CheckCircle2 className="h-10 w-10 text-ember" />
                <h2 className="mt-6 font-display text-2xl font-extrabold tracking-tight">
                  Request received.
                </h2>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-mist">
                  Thank you for contacting Petroline Services. We will review your request and
                  get back to you using your preferred contact method. For urgent matters, call{" "}
                  <a href={SITE.phoneHref} className="text-ember underline">
                    {SITE.phone}
                  </a>
                  .
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="border border-black/10 bg-white p-8 lg:p-10">
                <h2 className="font-display text-xl font-extrabold tracking-tight">Request Service</h2>
                <div className="mt-8 grid gap-6 sm:grid-cols-2">
                  <Field label="Name *" testId="field-name">
                    <input required data-testid="contact-name-input" className={inputClass} value={form.name} onChange={set("name")} placeholder="Your name" />
                  </Field>
                  <Field label="Company" testId="field-company">
                    <input data-testid="contact-company-input" className={inputClass} value={form.company} onChange={set("company")} placeholder="Company name" />
                  </Field>
                  <Field label="Phone *" testId="field-phone">
                    <input required type="tel" data-testid="contact-phone-input" className={inputClass} value={form.phone} onChange={set("phone")} placeholder="905-555-0000" />
                  </Field>
                  <Field label="Email *" testId="field-email">
                    <input required type="email" data-testid="contact-email-input" className={inputClass} value={form.email} onChange={set("email")} placeholder="you@company.com" />
                  </Field>
                  <Field label="Property / Station Location" testId="field-location">
                    <input data-testid="contact-location-input" className={inputClass} value={form.location} onChange={set("location")} placeholder="City or address" />
                  </Field>
                  <Field label="Service Required *" testId="field-service">
                    <select required data-testid="contact-service-select" className={inputClass} value={form.service} onChange={set("service")}>
                      <option value="" disabled>Select a service</option>
                      {SERVICES.map((s) => (
                        <option key={s.title} value={s.title}>{s.title}</option>
                      ))}
                      <option value="General Inquiry">General Inquiry / Other</option>
                    </select>
                  </Field>
                  <Field label="Preferred Contact Method" testId="field-contact-method">
                    <select data-testid="contact-method-select" className={inputClass} value={form.contact_method} onChange={set("contact_method")}>
                      <option value="Phone">Phone</option>
                      <option value="Email">Email</option>
                    </select>
                  </Field>
                  <div className="sm:col-span-2">
                    <Field label="Message *" testId="field-message">
                      <textarea required rows={5} data-testid="contact-message-input" className={inputClass} value={form.message} onChange={set("message")} placeholder="Tell us about the issue or project…" />
                    </Field>
                  </div>
                </div>

                <label className="mt-6 flex items-start gap-3" data-testid="field-consent">
                  <input
                    type="checkbox"
                    required
                    data-testid="contact-consent-checkbox"
                    checked={form.consent}
                    onChange={set("consent")}
                    className="mt-1 h-4 w-4 accent-[#F95738]"
                  />
                  <span className="text-xs leading-relaxed text-neutral-600">
                    I agree to be contacted by Petroline Services regarding my inquiry.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="contact-submit-btn"
                  className="group mt-8 inline-flex w-full items-center justify-center gap-3 bg-ember px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-coal transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60 sm:w-auto"
                >
                  {status === "sending" ? "Sending…" : "Submit Request"}
                  <Send className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Contact;
